import { useState } from 'react';
import { useLoaderData } from 'react-router';
import { DateFormat, formatDate } from '@/data/format/format-date';
import { formatCurrency } from '@repo/ui/lib/format/format-currency';
import { DataTable } from '@repo/ui/components/data-table';
import { Sale } from '@repo/shared-types';
import { Column, Row } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@repo/ui/components/data-table-column-header';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@repo/ui/components/dialog';
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableHeader,
} from '@repo/ui/components/table';
import { SalesDailySummary } from '../sales-daily-summary';
import { route } from './route';
import { useDebouncedQueryParam } from '@/hooks/use-debounced-query-param';
import { InputTextWithClear } from '@repo/ui/components/input-text-with-clear';

const columns = [
  {
    header: 'Descripción',
    cell: ({ row }: { row: Row<Sale> }) => {
      const description = row.original.items.map((item) => item.product.name).join(', ');
      return <div className="whitespace-normal max-w-xs">{description}</div>;
    },
  },
  {
    id: 'productIds',
    hidden: true,
    accessorFn: (row: Sale) => row.items.map((item) => item.product.id),
  },
  {
    hidden: true,
    id: 'searchtext',
    accessorFn: (row: Sale) => {
      const name = row.player?.name || row.items[0]?.name;
      const lastName = row.player?.lastName ?? '';
      return `${name} ${lastName}`;
    },
  },
  {
    header: 'Jugador',
    accessorKey: 'player',
    cell: ({ row }: { row: Row<Sale> }) => {
      const name = row.original.player?.name || row.original.items[0]?.name;
      const lastName = row.original.player?.lastName ?? '';
      return `${name} ${lastName}`;
    },
  },
  {
    accessorKey: 'amount',
    cell: ({ row }: { row: Row<Sale> }) => {
      return formatCurrency(row.original.amount);
    },
    headerTitle: 'Total',
    header: ({ column }: { column: Column<Sale> }) => (
      <DataTableColumnHeader column={column} title="Total" />
    ),
  },
  {
    accessorKey: 'createdAt',
    cell: ({ row }: { row: Row<Sale> }) => {
      return formatDate(row.original.createdAt, DateFormat.DD_MM_YYYY_HH_MM);
    },
    headerTitle: 'Fecha',
    header: ({ column }: { column: Column<Sale> }) => (
      <DataTableColumnHeader column={column} title="Fecha" />
    ),
    sortType: 'datetime',
  },
];

export function Sales() {
  const { sales } = useLoaderData<typeof route.loader>();
  const { value: player, setValue: setPlayer } = useDebouncedQueryParam('player');
  const { value: product, setValue: setProduct } = useDebouncedQueryParam('product');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);

  return (
    <div className="flex flex-col gap-4 h-full">
      <SalesDailySummary daySummary={sales.daySummary} />
      <DataTable
        total={sales.count}
        columns={columns}
        data={sales.results}
        onRowClick={(sale) => setSelectedSale(sale)}
        filters={() => (
          <div className="flex gap-2">
            <InputTextWithClear
              value={player}
              onChange={(value) => setPlayer(value)}
              onClear={() => setPlayer('')}
              placeholder="Buscar por jugador"
              className="w-48"
            />
            <InputTextWithClear
              value={product}
              onChange={(value) => setProduct(value)}
              onClear={() => setProduct('')}
              placeholder="Buscar por producto"
              className="w-48"
            />
          </div>
        )}
      />

      <Dialog open={!!selectedSale} onOpenChange={(open) => !open && setSelectedSale(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de venta</DialogTitle>
          </DialogHeader>

          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Jugador</span>
                  <p className="font-medium">
                    {selectedSale.player
                      ? `${selectedSale.player.name} ${selectedSale.player.lastName}`
                      : (selectedSale.items[0]?.name ?? '—')}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Fecha</span>
                  <p className="font-medium">
                    {formatDate(selectedSale.createdAt, DateFormat.DD_MM_YYYY_HH_MM)}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total</span>
                  <p className="font-medium">{formatCurrency(selectedSale.amount)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Punto de venta</span>
                  <p className="font-medium">
                    {selectedSale.items[0]?.pointOfSale?.name ?? '—'}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">Productos</h4>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead>Cant.</TableHead>
                        <TableHead className="text-right">Precio</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedSale.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.product.name}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell className="text-right">
                            {item.price != null ? formatCurrency(item.price) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter showCloseButton />
        </DialogContent>
      </Dialog>
    </div>
  );
}
